import React, { useState, useEffect, useCallback, Suspense, useContext } from "react";
import { Link, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import API from "../../../services/interceptor";
import Loading from "../../Loading";
import ToothGrid from "../Comman/ToothGrid";
import MedicationsSection from "../Comman/MedicalForm/MedicationsSection";
import AllergiesSection from "../Comman/MedicalForm/AllergiesSection";
import { AuthContext } from "../../../context/AuthContext";

const AdminCheckup = () => {

    const {primaryDiagnosis, treatmentPlan, visitReason } = useContext(AuthContext);


  const { id } = useParams();

  // Loading states
  const [initialLoading, setInitialLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  // Success and error messages
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [opdForm, setOPDForm] = useState({});
  const [selectedTeeth, setSelectedTeeth] = useState([]);

  // Patient Report State
  const [patientReport, setPatientReport] = useState({
    opdNumber: "",
    examinationFindings: {
      teethCondition: "",
      gumsCondition: "Select Conditions...",
      allergies: [{ allergen: "", reaction: "", severity: "" }],
      oralHygieneLevel: "",
      notes: "",
      selectedTeeth: selectedTeeth,
    },

    diagnosis: {
      primaryDiagnosis: "",
      secondaryDiagnosis: "",
    },
    treatmentPlan: {
      treatmentSuggested: "",
      immediateProceduresPerformed: "",
      prescribedMedications: [
        { name: "", dosage: "", frequency: "", startDate: "", endDate: "" },
      ],
    },
    additionalNotes: "",
    financials: {
      billingItems: [
        {
          name: "",
          quantity: 1,
          pricePerItem: 0,
          totalCostPerItem: 0,
        },
      ],
      totalCost: 0,
      visits: [
        {
          treatmentDone: false,
          date: "",
          visitFor: "",
          amount: 0,
          notes: "",
        },
      ],
    },
    documents: [
      {
        name: "",
        url: "",
      },
    ],
    createdAt: "",
  });

  // Fetch existing Patient Report data
  useEffect(() => {
    const fetchPatientReport = async () => {
      try {
        const response = await API.get(`/api/doctor/details/${id}`);
        setOPDForm(response.data);
        setPatientReport(response.data.patientReport);

        // Handle selectedTeeth with backward compatibility
        const loadedSelectedTeeth =
          response.data.patientReport.examinationFindings.selectedTeeth || [];

        // Convert old format to new format if needed
        const convertedTeeth = loadedSelectedTeeth.map((tooth) => {
          if (tooth.reason && !tooth.reasons) {
            // Convert old single reason format to new multiple reasons format
            return {
              id: tooth.id,
              reasons: [tooth.reason],
            };
          } else if (tooth.reasons) {
            // Already in new format
            return tooth;
          } else {
            // No reasons at all
            return {
              id: tooth.id,
              reasons: [],
            };
          }
        });

        setSelectedTeeth(convertedTeeth);
        setInitialLoading(false);
      } catch (err) {
        console.error("Error fetching Patient Report:", err.message);
        setError(
          err.response?.data?.message ||
            "Failed to load the patient report. Please try again."
        );
        setInitialLoading(false);
      }
    };

    fetchPatientReport();
  }, [id]);

  // Handle document upload
  const handleDocumentUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "mypreset"); // Replace with your upload preset
    formData.append("cloud_name", "dsi1mb2aj"); // Replace with your cloud name

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/dsi1mb2aj/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );
      const data = await response.json();
      const documentUrl = data.secure_url;

      // Update the document URL in the state
      setPatientReport((prevState) => {
        const updatedDocuments = [...prevState.documents];
        updatedDocuments[index].url = documentUrl;
        return { ...prevState, documents: updatedDocuments };
      });

      toast.success("Document uploaded successfully!", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    } catch (error) {
      console.error("Error uploading document:", error);
      toast.error("Failed to upload document.", {
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });
    }
  };

  // Handle changes for nested objects
  const handleNestedChange = (
    e,
    section,
    field,
    index = null,
    subField = null
  ) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setPatientReport((prevState) => {
      if (section === "documents") {
        const updatedDocuments = [...prevState.documents];
        if (index !== null && subField) {
          updatedDocuments[index][subField] = value;
        }
        return { ...prevState, documents: updatedDocuments };
      }

      const updatedSection = { ...prevState[section] };

      if (section === "examinationFindings" || section === "diagnosis") {
        updatedSection[field] = value;
      } else if (section === "treatmentPlan") {
        if (field === "prescribedMedications" && index !== null && subField) {
          const meds = [...updatedSection.prescribedMedications];
          meds[index][subField] = value;
          updatedSection.prescribedMedications = meds;
        } else {
          updatedSection[field] = value;
        }
      } else if (section === "financials") {
        if (field === "visits" && index !== null && subField) {
          const visits = [...updatedSection.visits];
          visits[index][subField] = value;
          updatedSection.visits = visits;
        } else {
          updatedSection[field] = value;
        }
      }

      return { ...prevState, [section]: updatedSection };
    });
  };

  // Handle changes for simple fields
  const handleSimpleChange = (e) => {
    const { name, value } = e.target;
    setPatientReport((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Handle changes to selectedTeeth - Updated for multiple reasons
  useEffect(() => {
    console.log(selectedTeeth);

    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        selectedTeeth, // Update the selectedTeeth in the report
      },
    }));
  }, [selectedTeeth]); // Trigger this effect whenever selectedTeeth changes

  // Prescribed Medications Handlers
  const handleMedicationChange = (e, index, field) => {
    const value = e.target.value;
    setPatientReport((prevState) => {
      const meds = [...prevState.treatmentPlan.prescribedMedications];
      meds[index][field] = value;
      return {
        ...prevState,
        treatmentPlan: {
          ...prevState.treatmentPlan,
          prescribedMedications: meds,
        },
      };
    });
  };

  const addMedication = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      treatmentPlan: {
        ...prevState.treatmentPlan,
        prescribedMedications: [
          ...prevState.treatmentPlan.prescribedMedications,
          { name: "", dosage: "", frequency: "", startDate: "", endDate: "" },
        ],
      },
    }));
  };

  const removeMedication = (index) => {
    setPatientReport((prevState) => {
      const meds = [...prevState.treatmentPlan.prescribedMedications];
      meds.splice(index, 1);
      return {
        ...prevState,
        treatmentPlan: {
          ...prevState.treatmentPlan,
          prescribedMedications:
            meds.length > 0
              ? meds
              : [
                  {
                    name: "",
                    dosage: "",
                    frequency: "",
                    startDate: "",
                    endDate: "",
                  },
                ],
        },
      };
    });
  };

  // Financial Visits Handlers
  const addVisit = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      financials: {
        ...prevState.financials,
        visits: [
          ...prevState.financials.visits,
          {
            treatmentDone: false,
            date: "",
            visitFor: "",
            amount: 0,
            notes: "",
          },
        ],
      },
    }));
  };

  const removeVisit = (index) => {
    setPatientReport((prevState) => {
      const visits = [...prevState.financials.visits];
      visits.splice(index, 1);
      return {
        ...prevState,
        financials: {
          ...prevState.financials,
          visits:
            visits.length > 0
              ? visits
              : [
                  {
                    treatmentDone: false,
                    date: "",
                    visitFor: "",
                    amount: 0,
                    notes: "",
                  },
                ],
        },
      };
    });
  };

  // Documents Handlers
  const addDocument = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      documents: [...prevState.documents, { name: "", url: "" }],
    }));
  };

  const removeDocument = (index) => {
    setPatientReport((prevState) => {
      const docs = [...prevState.documents];
      docs.splice(index, 1);
      return {
        ...prevState,
        documents: docs.length > 0 ? docs : [{ name: "", url: "" }],
      };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Show loading toast
    const loadingToastId = toast.loading("Updating Patient Report...", {
      position: "top-center",
      hideProgressBar: false,
      theme: "dark",
    });

    console.log("Patient Report with Multiple Reasons:", patientReport);

    try {
      await API.put(`/api/doctor/patient-reports/${id}`, patientReport);

      // Update the loading toast to success
      toast.update(loadingToastId, {
        render: "Update Patient Report Successfully",
        type: "success",
        isLoading: false,
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });

      setSuccess("Patient Report updated successfully!");
      setError("");
    } catch (err) {
      console.error(err);

      // Update the loading toast to error
      toast.update(loadingToastId, {
        render: "Failed To Update Patient Report",
        type: "error",
        isLoading: false,
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: true,
        theme: "dark",
      });

      setError(
        err.response?.data?.message ||
          "Failed to update the patient report. Please try again."
      );
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  //billing part
  // Billing Form for the billingItems array
  const handleBillingItemChange = (e, index, field) => {
    const value = e.target.value;
    const updatedBillingItems = [...patientReport.financials.billingItems];
    updatedBillingItems[index][field] =
      field === "quantity" || field === "pricePerItem"
        ? parseFloat(value)
        : value;

    // Recalculate totalCostPerItem for that item
    updatedBillingItems[index].totalCostPerItem =
      updatedBillingItems[index].quantity *
      updatedBillingItems[index].pricePerItem;

    // Update the financial state
    setPatientReport((prevState) => {
      const totalCost = updatedBillingItems.reduce(
        (acc, item) => acc + item.totalCostPerItem,
        0
      );

      return {
        ...prevState,
        financials: {
          ...prevState.financials,
          billingItems: updatedBillingItems,
          totalCost: totalCost,
        },
      };
    });
  };

  const addBillingItem = () => {
    setPatientReport((prevState) => ({
      ...prevState,
      financials: {
        ...prevState.financials,
        billingItems: [
          ...prevState.financials.billingItems,
          { name: "", quantity: 1, pricePerItem: 0, totalCostPerItem: 0 },
        ],
      },
    }));
  };

  const removeBillingItem = (index) => {
    setPatientReport((prevState) => {
      const updatedBillingItems = [...prevState.financials.billingItems];
      updatedBillingItems.splice(index, 1);

      // Recalculate totalCost
      const totalCost = updatedBillingItems.reduce(
        (acc, item) => acc + item.totalCostPerItem,
        0
      );

      return {
        ...prevState,
        financials: {
          ...prevState.financials,
          billingItems: updatedBillingItems,
          totalCost: totalCost,
        },
      };
    });
  };

  // Helper function to display teeth with multiple reasons
  const renderSelectedTeethWithReasons = () => {
    if (selectedTeeth.length === 0) return null;

    return (
      <div className="mt-8">
        <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg
                className="w-4 h-4 text-blue-600"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              Selected Teeth for Treatment
            </h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
              {selectedTeeth.length}{" "}
              {selectedTeeth.length === 1 ? "tooth" : "teeth"}
            </span>
          </div>

          {selectedTeeth.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedTeeth.map((tooth) => (
                <div
                  key={tooth.id}
                  className="bg-white rounded-lg border border-slate-200 p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-600 text-white rounded-lg flex items-center justify-center text-sm font-semibold">
                        {tooth.id}
                      </div>
                      <span className="font-medium text-slate-900">
                        Tooth {tooth.id}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        // Add functionality to remove tooth if needed
                        const updatedTeeth = selectedTeeth.filter(
                          (t) => t.id !== tooth.id
                        );
                        setSelectedTeeth(updatedTeeth);
                      }}
                      className="text-slate-400 hover:text-red-500 transition-colors"
                      aria-label={`Remove tooth ${tooth.id}`}
                    >
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">
                      Treatment Reasons
                    </label>
                    {tooth.reasons && tooth.reasons.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {tooth.reasons.map((reason, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 bg-amber-50 text-amber-700 text-xs font-medium rounded-md border border-amber-200"
                          >
                            {reason}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <svg
                          className="w-3 h-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                            clipRule="evenodd"
                          />
                        </svg>
                        No treatment reasons specified
                      </div>
                    )}
                  </div>

                  {/* Optional: Add procedure or severity indicators */}
                  {/* <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Status:</span>
                <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded-full font-medium">
                  Requires Treatment
                </span>
              </div>
            </div> */}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-slate-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h4 className="text-slate-900 font-medium mb-2">
                No teeth selected
              </h4>
              <p className="text-slate-500 text-sm">
                Use the dental chart above to select teeth that require
                treatment
              </p>
            </div>
          )}

          {/* Summary Section */}
          {selectedTeeth.length > 0 && (
            <div className="mt-6 pt-4 border-t border-slate-200">
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">Total teeth selected:</span>
                  <span className="font-semibold text-slate-900">
                    {selectedTeeth.length}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-slate-600">
                    With treatment reasons:
                  </span>
                  <span className="font-semibold text-slate-900">
                    {
                      selectedTeeth.filter(
                        (tooth) => tooth.reasons && tooth.reasons.length > 0
                      ).length
                    }
                  </span>
                </div>
                {/* <div className="flex items-center gap-2">
            <span className="text-slate-600">Requiring attention:</span>
            <span className="font-semibold text-slate-900">
              {selectedTeeth.filter(tooth => !tooth.reasons || tooth.reasons.length === 0).length}
            </span>
          </div> */}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Handle changes for nested arrays (allergies)
  const handleAllergiesChange = useCallback((section, index, field, value) => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        [section]: prevState.examinationFindings[section].map((item, idx) =>
          idx === index ? { ...item, [field]: value } : item
        ),
      },
    }));
  }, []);

  // Add allergy entry
  const addAllergy = useCallback(() => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        allergies: [
          ...prevState.examinationFindings.allergies,
          { allergen: "", reaction: "", severity: "" },
        ],
      },
    }));
  }, []);

  // Remove allergy entry
  const removeAllergy = useCallback((index) => {
    setPatientReport((prevState) => ({
      ...prevState,
      examinationFindings: {
        ...prevState.examinationFindings,
        allergies: prevState.examinationFindings.allergies.filter(
          (_, idx) => idx !== index
        ),
      },
    }));
  }, []);

  return (
    <div className="min-h-screen  ">
      <div className=" mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 mb-8 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">
                Patient Medical Record
              </h1>
              <p className="text-slate-600 mt-1">
                {opdForm?.userDetails?.name} • OPD #{patientReport.opdNumber}
                {console.log(opdForm, "opdFormopdFormopdFormopdForm")}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Created</p>
              <p className="text-slate-700 font-medium">
                {patientReport.createdAt
                  ? (() => {
                      const date = new Date(patientReport.createdAt);
                      const day = String(date.getDate()).padStart(2, "0");
                      const month = String(date.getMonth() + 1).padStart(
                        2,
                        "0"
                      );
                      const year = date.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()
                  : (() => {
                      const today = new Date();
                      const day = String(today.getDate()).padStart(2, "0");
                      const month = String(today.getMonth() + 1).padStart(
                        2,
                        "0"
                      );
                      const year = today.getFullYear();
                      return `${day}/${month}/${year}`;
                    })()}
              </p>
            </div>
          </div>
        </div>

        {initialLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loading />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Patient Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Patient Overview
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    OPD Number
                  </label>
                  <input
                    type="text"
                    value={patientReport.opdNumber}
                    readOnly
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-600"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">
                    Chief Complaint
                  </label>
                  <input
                    type="text"
                    value={opdForm?.checkupInfo || "Not specified"}
                    readOnly
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-600"
                  />
                </div>
              </div>
            </div>

            {/* Clinical Examination */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-emerald-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Clinical Examination
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Gums Condition */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Gums Condition <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={patientReport.examinationFindings.gumsCondition}
                    onChange={(e) =>
                      handleNestedChange(
                        e,
                        "examinationFindings",
                        "gumsCondition"
                      )
                    }
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select condition...</option>
                    <option value="Bleeding gum">Bleeding Gum</option>
                    <option value="Healthy gum">Healthy Gum</option>
                    <option value="Swollen Gum">Swollen Gum</option>
                  </select>
                </div>

                {/* Oral Hygiene Level */}
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Oral Hygiene Level <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-5 gap-3">
                    {["Very Poor", "Poor", "Fair", "Good", "Excellent"].map(
                      (level) => (
                        <label
                          key={level}
                          className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                        >
                          <input
                            type="radio"
                            name="oralHygieneLevel"
                            value={level}
                            checked={
                              patientReport.examinationFindings
                                .oralHygieneLevel === level
                            }
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "examinationFindings",
                                "oralHygieneLevel"
                              )
                            }
                            required
                            className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                          />
                          <span className="text-sm text-slate-700">
                            {level}
                          </span>
                        </label>
                      )
                    )}
                  </div>
                </div>
              </div>

              {/* Dental Chart */}
              <div className="mt-8 space-y-4">
                <label className="text-sm font-medium text-slate-700">
                  Dental Chart
                </label>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <ToothGrid
                    selectedTeeth={selectedTeeth}
                    setSelectedTeeth={setSelectedTeeth}
                    patientAge={opdForm?.age}
                  />
                </div>
                {renderSelectedTeethWithReasons()}
              </div>

              {/* Clinical Notes */}
              <div className="mt-6 space-y-3">
                <label className="text-sm font-medium text-slate-700">
                  Clinical Notes
                </label>
                <textarea
                  value={patientReport.examinationFindings.notes || ""}
                  onChange={(e) =>
                    handleNestedChange(e, "examinationFindings", "notes")
                  }
                  rows="4"
                  placeholder="Additional examination findings and observations..."
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Allergies Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Patient Allergies
                </h2>
              </div>

              <Suspense
                fallback={
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                }
              >
                <AllergiesSection
                  allergies={
                    patientReport.examinationFindings?.allergies || [
                      { allergen: "", reaction: "", severity: "" },
                    ]
                  }
                  onChange={handleAllergiesChange}
                  onAdd={addAllergy}
                  onRemove={removeAllergy}
                />
              </Suspense>
            </div>

            {/* Diagnosis */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-amber-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Diagnosis
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Primary Diagnosis <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={patientReport.diagnosis.primaryDiagnosis || ""}
                    onChange={(e) =>
                      handleNestedChange(e, "diagnosis", "primaryDiagnosis")
                    }
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="" disabled>
                      Select primary diagnosis...
                    </option>
                    {primaryDiagnosis.map((diagnosis, index) => (
                      <option key={index} value={diagnosis.Diagnosis}>
                        {diagnosis.Diagnosis}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Secondary Diagnosis
                  </label>
                  <input
                    type="text"
                    value={patientReport.diagnosis.secondaryDiagnosis || ""}
                    onChange={(e) =>
                      handleNestedChange(e, "diagnosis", "secondaryDiagnosis")
                    }
                    placeholder="Additional diagnosis (optional)"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Treatment Plan */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-purple-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Treatment Plan
                </h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Recommended Treatment{" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={patientReport.treatmentPlan.treatmentSuggested || ""}
                    onChange={(e) =>
                      handleNestedChange(
                        e,
                        "treatmentPlan",
                        "treatmentSuggested"
                      )
                    }
                    required
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="" disabled>
                      Select treatment plan...
                    </option>
                    {treatmentPlan.map((treatment, index) => (
                      <option key={index} value={treatment}>
                        {treatment}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="text-sm font-medium text-slate-700">
                    Procedures Performed Today
                  </label>
                  <input
                    type="text"
                    value={
                      patientReport.treatmentPlan
                        .immediateProceduresPerformed || ""
                    }
                    onChange={(e) =>
                      handleNestedChange(
                        e,
                        "treatmentPlan",
                        "immediateProceduresPerformed"
                      )
                    }
                    placeholder="Procedures completed during this visit"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Medications */}
              {/* <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">Prescribed Medications</label>
                  <button
                    type="button"
                    onClick={addMedication}
                    className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                    </svg>
                    Add Medication
                  </button>
                </div>

                <div className="space-y-4">
                  {patientReport.treatmentPlan.prescribedMedications.map((med, index) => (
                    <div key={index} className="p-4 border border-slate-200 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">Medication Name</label>
                          <input
                            type="text"
                            placeholder="Medicine name"
                            value={med.name}
                            onChange={(e) => handleMedicationChange(e, index, "name")}
                            required
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">Dosage</label>
                          <input
                            type="text"
                            placeholder="e.g., 500mg"
                            value={med.dosage}
                            onChange={(e) => handleMedicationChange(e, index, "dosage")}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">Frequency</label>
                          <input
                            type="text"
                            placeholder="e.g., 2x daily"
                            value={med.frequency}
                            onChange={(e) => handleMedicationChange(e, index, "frequency")}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">Start Date</label>
                          <input
                            type="date"
                            value={med.startDate ? med.startDate.substring(0, 10) : ""}
                            onChange={(e) => handleMedicationChange(e, index, "startDate")}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-600">End Date</label>
                          <input
                            type="date"
                            value={med.endDate ? med.endDate.substring(0, 10) : ""}
                            onChange={(e) => handleMedicationChange(e, index, "endDate")}
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                      {patientReport.treatmentPlan.prescribedMedications.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeMedication(index)}
                          className="mt-3 text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          Remove Medication
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div> */}

              {/* Medications - Table Format */}
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              {" "}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-slate-700">
                    Prescription
                  </label>
                </div>

                <div className="space-y-4 border border-slate-200 rounded-lg">
                  {/* Table Header */}
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-slate-100 rounded-lg font-medium text-center">
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Medication Name
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Dosage
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Meal Timing
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Time of Day
                    </div>
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                      Duration (Days)
                    </div>
                  </div>

                  {/* Table Rows */}
                  <div className="space-y-2">
                    {patientReport.treatmentPlan.prescribedMedications.map(
                      (med, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 md:grid-cols-5 gap-4 p-4 bg-white hover:bg-slate-50 transition-colors"
                        >
                          <div>
                            <input
                              type="text"
                              placeholder="Medicine name"
                              value={med.name}
                              onChange={(e) =>
                                handleMedicationChange(e, index, "name")
                              }
                              required
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div>
                            <input
                              type="text"
                              placeholder="e.g., 500mg"
                              value={med.dosage}
                              onChange={(e) =>
                                handleMedicationChange(e, index, "dosage")
                              }
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                          </div>
                          <div className="flex justify-center">
                            <div className="flex flex-col gap-2">
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="radio"
                                  name={`mealTiming-${index}`}
                                  value="before_meal"
                                  checked={med.mealTiming === "before_meal"}
                                  onChange={(e) =>
                                    handleMedicationChange(
                                      e,
                                      index,
                                      "mealTiming"
                                    )
                                  }
                                  className="mr-2 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">
                                  Before Meal
                                </span>
                              </label>
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="radio"
                                  name={`mealTiming-${index}`}
                                  value="after_meal"
                                  checked={med.mealTiming === "after_meal"}
                                  onChange={(e) =>
                                    handleMedicationChange(
                                      e,
                                      index,
                                      "mealTiming"
                                    )
                                  }
                                  className="mr-2 text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm text-slate-700">
                                  After Meal
                                </span>
                              </label>
                            </div>
                          </div>
                          <div className="flex items-center justify-center">
                            <div className="flex flex-col gap-3 justify-center">
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={
                                    med.timeOfDay?.includes("morning") || false
                                  }
                                  onChange={(e) => {
                                    const currentTimes = med.timeOfDay || [];
                                    const newTimes = e.target.checked
                                      ? [...currentTimes, "morning"]
                                      : currentTimes.filter(
                                          (time) => time !== "morning"
                                        );
                                    handleMedicationChange(
                                      { target: { value: newTimes } },
                                      index,
                                      "timeOfDay"
                                    );
                                  }}
                                  className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  Morning
                                </span>
                              </label>
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={
                                    med.timeOfDay?.includes("afternoon") ||
                                    false
                                  }
                                  onChange={(e) => {
                                    const currentTimes = med.timeOfDay || [];
                                    const newTimes = e.target.checked
                                      ? [...currentTimes, "afternoon"]
                                      : currentTimes.filter(
                                          (time) => time !== "afternoon"
                                        );
                                    handleMedicationChange(
                                      { target: { value: newTimes } },
                                      index,
                                      "timeOfDay"
                                    );
                                  }}
                                  className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  Afternoon
                                </span>
                              </label>
                              <label className="flex items-center whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={
                                    med.timeOfDay?.includes("evening") || false
                                  }
                                  onChange={(e) => {
                                    const currentTimes = med.timeOfDay || [];
                                    const newTimes = e.target.checked
                                      ? [...currentTimes, "evening"]
                                      : currentTimes.filter(
                                          (time) => time !== "evening"
                                        );
                                    handleMedicationChange(
                                      { target: { value: newTimes } },
                                      index,
                                      "timeOfDay"
                                    );
                                  }}
                                  className="mr-2 text-blue-600 focus:ring-blue-500 rounded"
                                />
                                <span className="text-sm text-slate-700">
                                  Evening
                                </span>
                              </label>
                            </div>
                          </div>
                          <div>
                            <input
                              type="number"
                              placeholder="e.g., 7"
                              value={med.durationDays || ""}
                              onChange={(e) =>
                                handleMedicationChange(e, index, "durationDays")
                              }
                              min="1"
                              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            {patientReport.treatmentPlan.prescribedMedications
                              .length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeMedication(index)}
                                className="ml-2 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                                title="Remove medication"
                              >
                                <svg
                                  className="w-4 h-4"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  <button
                    type="button"
                    onClick={addMedication}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                    </svg>
                    Add Medication
                  </button>
                </div>
              </div>
            </div>

            {/* Cost Estimation */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.51-1.31c-.562-.649-1.413-1.076-2.353-1.253V5z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Treatment Cost Estimation
                </h2>
              </div>

              <div className="space-y-4 border border-slate-200 rounded-lg">
                {/* Table Header */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-slate-100 rounded-lg font-medium ">
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Procedure
                  </div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Quantity
                  </div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Unit Cost
                  </div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                    Total
                  </div>
                </div>

                {/* Table Rows */}
                <div className="space-y-2">
                  {patientReport.financials.billingItems.map((item, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-white  hover:bg-slate-50 transition-colors"
                    >
                      <div>
                        {/* <input
                          type="text"
                          placeholder="Enter procedure name"
                          value={item.name}
                          onChange={(e) =>
                            handleBillingItemChange(e, index, "name")
                          }
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        /> */}

                        <select
                          value={item.name}
                          onChange={(e) =>
                            handleBillingItemChange(e, index, "name")
                          }
                          required
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="" disabled>
                            Select treatment plan...
                          </option>
                          {treatmentPlan.map((treatment, index) => (
                            <option key={index} value={treatment}>
                              {treatment}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) =>
                            handleBillingItemChange(e, index, "quantity")
                          }
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.pricePerItem}
                          onChange={(e) =>
                            handleBillingItemChange(e, index, "pricePerItem")
                          }
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="flex items-center justify-between">
                        <input
                          type="number"
                          value={item.totalCostPerItem}
                          readOnly
                          className="w-full px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg text-slate-600 font-medium"
                        />
                        {patientReport.financials.billingItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBillingItem(index)}
                            className="ml-2 p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            title="Remove item"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add Item Button */}
                <button
                  type="button"
                  onClick={addBillingItem}
                  className="inline-flex items-center gap-2 px-4 py-2 my-4 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors border border-green-200"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Item
                </button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-slate-700">
                    Total Estimated Cost:
                  </span>
                  <span className="text-2xl font-bold text-blue-700">
                    ₹{patientReport?.financials?.totalCost?.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* Visit History */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-indigo-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Treatment Sessions
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addVisit}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Session
                </button>
              </div>

              <div className="space-y-4">
                {patientReport.financials.visits.map((visit, index) => (
                  <div
                    key={index}
                    className="p-4 border border-slate-200 rounded-lg"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Treatment Completed
                        </label>
                        <div className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={visit.treatmentDone === true}
                              onChange={(e) =>
                                handleNestedChange(
                                  { target: { value: e.target.checked } },
                                  "financials",
                                  "visits",
                                  index,
                                  "treatmentDone"
                                )
                              }
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded mr-3"
                            />
                            <span
                              className={`text-sm ${
                                visit.treatmentDone === true
                                  ? "text-green-600 font-medium"
                                  : "text-slate-500"
                              }`}
                            >
                              {visit.treatmentDone === true
                                ? "Yes, Done"
                                : "No, Still not"}
                            </span>
                          </label>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={visit.date ? visit.date.substring(0, 10) : ""}
                          onChange={(e) =>
                            handleNestedChange(
                              e,
                              "financials",
                              "visits",
                              index,
                              "date"
                            )
                          }
                          required
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Purpose <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={visit.visitFor || ""}
                          onChange={(e) =>
                            handleNestedChange(
                              e,
                              "financials",
                              "visits",
                              index,
                              "visitFor"
                            )
                          }
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="" disabled>
                            Select purpose...
                          </option>
                          {visitReason.map((reason) => (
                            <option key={reason} value={reason}>
                              {reason}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Amount Paid <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={visit.amount}
                          onChange={(e) =>
                            handleNestedChange(
                              e,
                              "financials",
                              "visits",
                              index,
                              "amount"
                            )
                          }
                          required
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                    <div className="mt-4 space-y-2">
                      <label className="text-xs font-medium text-slate-600">
                        Session Notes
                      </label>
                      <input
                        type="text"
                        placeholder="Additional notes for this session..."
                        value={visit.notes || ""}
                        onChange={(e) =>
                          handleNestedChange(
                            e,
                            "financials",
                            "visits",
                            index,
                            "notes"
                          )
                        }
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    {patientReport.financials.visits.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeVisit(index)}
                        className="mt-3 text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove Session
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Documents */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <svg
                      className="w-4 h-4 text-orange-600"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h2 className="text-lg font-semibold text-slate-900">
                    Clinical Documents
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={addDocument}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100 transition-colors"
                >
                  <svg
                    className="w-4 h-4"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" />
                  </svg>
                  Add Document
                </button>
              </div>

              <div className="space-y-4">
                {patientReport.documents.map((doc, index) => (
                  <div
                    key={index}
                    className="p-4 border border-slate-200 rounded-lg"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Document Name
                        </label>
                        <select
                          value={doc.name}
                          onChange={(e) =>
                            handleNestedChange(
                              e,
                              "documents",
                              null,
                              index,
                              "name"
                            )
                          }
                          required
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                        >
                          <option value="">Select document type</option>
                          <option value="X-Ray">X-Ray</option>
                          <option value="Concern Form">Concern Form</option>
                          <option value="Before Treatment Photo">
                            Before Treatment Photo
                          </option>
                          <option value="After Treatment Photo">
                            After Treatment Photo
                          </option>
                          <option value="Others">Others</option>
                        </select>

                        {/* Show text input only when "Others" is selected */}
                        {doc.name === "Others" && (
                          <input
                            type="text"
                            placeholder="Enter custom document name"
                            value={doc.customName || ""}
                            onChange={(e) =>
                              handleNestedChange(
                                e,
                                "documents",
                                null,
                                index,
                                "customName"
                              )
                            }
                            className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mt-2"
                          />
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="text-xs font-medium text-slate-600">
                          Upload Document
                        </label>
                        <input
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={(e) => handleDocumentUpload(e, index)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-sm file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>
                    {doc.url && (
                      <div className="mt-3">
                        <Link
                          to={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z" />
                            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                          </svg>
                          View Document
                        </Link>
                      </div>
                    )}
                    {patientReport.documents.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDocument(index)}
                        className="mt-3 text-red-600 hover:text-red-700 text-sm font-medium"
                      >
                        Remove Document
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Additional Notes */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <svg
                    className="w-4 h-4 text-gray-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <h2 className="text-lg font-semibold text-slate-900">
                  Additional Notes
                </h2>
              </div>

              <textarea
                name="additionalNotes"
                value={patientReport.additionalNotes || ""}
                onChange={handleSimpleChange}
                placeholder="Any additional observations, follow-up instructions, or special notes..."
                rows="4"
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <button
                type="submit"
                disabled={loading}
                className={`
             w-full sm:w-auto px-8 py-3 bg-blue-600 text-white font-semibold rounded-lg
             hover:bg-blue-700 focus:ring-4 focus:ring-blue-200 transition-all duration-200
             disabled:opacity-50 disabled:cursor-not-allowed
             flex items-center justify-center gap-2
           `}
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" />
                    </svg>
                    Updating Record...
                  </>
                ) : (
                  <>
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Update Patient Record
                  </>
                )}
              </button>
            </div>

            {/* Status Messages */}
            {success && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-green-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-green-800 font-medium">{success}</p>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <p className="text-red-800 font-medium">{error}</p>
                </div>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminCheckup;
